import React, { useEffect, useState } from "react";
import { BookOpen, Building2, CheckCircle2, Copy, CreditCard, Headphones, Smartphone, Trash2, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../api";
import { toast } from "sonner";

const FORMAT_ICONS = { ebook: BookOpen, audiobook: Headphones, aplikacja: Smartphone };

export function CartDrawer() {
  const { items, remove, total, open, setOpen, checkout, checkingOut } = useCart();
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState("stripe");
  const [bank, setBank] = useState(null);
  const [transferDone, setTransferDone] = useState(null);

  useEffect(() => {
    if (open && !bank) api.get("/store/settings").then((r) => setBank(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => { if (!open) setTransferDone(null); }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (method === "transfer" && !email.includes("@")) {
      toast.error("Przy przelewie podaj adres e-mail — na niego wyślemy produkt.");
      return;
    }
    const data = await checkout(email, method);
    if (data && data.payment_method === "transfer") setTransferDone(data);
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success("Skopiowano do schowka"));
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-cyan-500/15 bg-slate-950 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-5">
          <h2 className="font-display text-lg font-bold">Twój koszyk</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close-button" className="rounded-full p-2 text-slate-400 transition-colors hover:text-white" aria-label="Zamknij koszyk">
            <X className="h-5 w-5" />
          </button>
        </div>

        {transferDone ? (
          <div className="flex-1 overflow-y-auto p-6" data-testid="transfer-confirmation">
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
              <h3 className="mt-3 font-display text-lg font-extrabold text-white">Zamówienie przyjęte!</h3>
              <p className="mt-1 text-sm text-slate-400">Numer zamówienia: <span className="font-mono font-bold text-cyan-300">{transferDone.order_id}</span></p>
            </div>
            <div className="mt-4 rounded-2xl glass-panel p-5">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Dane do przelewu</div>
              <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-slate-900 px-4 py-3">
                <span className="font-mono text-sm font-bold text-cyan-200" data-testid="transfer-account">{transferDone.bank_account}</span>
                <button onClick={() => copy(transferDone.bank_account)} className="rounded-lg p-2 text-slate-400 hover:text-cyan-300" aria-label="Kopiuj numer konta" data-testid="transfer-copy-account"><Copy className="h-4 w-4" /></button>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-300">
                <div className="flex justify-between"><span className="text-slate-500">Odbiorca:</span><span className="font-semibold">{transferDone.company_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Kwota:</span><span className="font-bold text-white">{transferDone.total.toFixed(2)} zł</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Tytuł przelewu:</span><span className="font-mono font-semibold text-cyan-300">{transferDone.order_id}</span></div>
              </div>
            </div>
            <p className="mt-4 rounded-xl border border-cyan-500/25 bg-cyan-500/10 p-4 text-xs leading-relaxed text-cyan-200">
              Przy tej formie płatności produkt wysyłamy <b>natychmiast po otrzymaniu płatności</b> na nasze konto — na adres <b>{email}</b>.
            </p>
            <button onClick={() => setOpen(false)} data-testid="transfer-close" className="mt-5 w-full rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3.5 font-display text-sm font-bold text-slate-950 transition-transform hover:scale-[1.02] active:scale-95">
              Rozumiem, dziękuję
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto p-6">
              {items.length === 0 && <p className="text-center text-sm text-slate-500">Koszyk jest pusty. Czas to zmienić!</p>}
              {items.map((i) => {
                const Icon = FORMAT_ICONS[i.format] || BookOpen;
                return (
                  <div key={i.id} className="flex items-center gap-3.5 rounded-xl glass-panel p-3" data-testid={`cart-item-${i.id}`}>
                    {i.cover_image ? (
                      <img src={i.cover_image} alt={i.title} className="h-16 w-12 shrink-0 rounded-lg object-cover shadow-lg" />
                    ) : (
                      <div className="flex h-16 w-12 shrink-0 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-400">
                        <Icon className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-slate-100">{i.title}</div>
                      <div className="text-xs text-slate-400">{i.author}</div>
                      <div className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">{i.format} · 1 szt.</div>
                    </div>
                    <div className="text-sm font-bold text-cyan-300">{i.price.toFixed(2)} zł</div>
                    <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="rounded-lg p-1.5 text-slate-500 transition-colors hover:text-rose-400" aria-label="Usuń">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-slate-800 p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm text-slate-400">Razem</span>
                <span data-testid="cart-total" className="font-display text-xl font-extrabold text-white">{total.toFixed(2)} zł</span>
              </div>

              <input
                data-testid="cart-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email do wysyłki produktu"
                className="mb-3 w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors focus:border-cyan-500"
              />

              <div className="mb-3 grid grid-cols-2 gap-2.5" data-testid="payment-methods">
                <button
                  data-testid="pay-method-stripe"
                  onClick={() => setMethod("stripe")}
                  className={`rounded-xl border p-3 text-left transition-all ${method === "stripe" ? "border-cyan-400/70 bg-cyan-500/10 shadow-[0_8px_24px_-8px_rgba(6,182,212,.5)]" : "border-slate-700 bg-slate-900/60 hover:border-slate-600"}`}
                >
                  <CreditCard className={`h-5 w-5 ${method === "stripe" ? "text-cyan-300" : "text-slate-400"}`} />
                  <div className="mt-1.5 text-xs font-bold text-slate-100">Karta · BLIK · P24</div>
                  <div className="text-[10px] text-slate-500">płatność obsługuje Stripe</div>
                </button>
                <button
                  data-testid="pay-method-transfer"
                  onClick={() => setMethod("transfer")}
                  className={`rounded-xl border p-3 text-left transition-all ${method === "transfer" ? "border-teal-400/70 bg-teal-500/10 shadow-[0_8px_24px_-8px_rgba(45,212,191,.5)]" : "border-slate-700 bg-slate-900/60 hover:border-slate-600"}`}
                >
                  <Building2 className={`h-5 w-5 ${method === "transfer" ? "text-teal-300" : "text-slate-400"}`} />
                  <div className="mt-1.5 text-xs font-bold text-slate-100">Przelew na konto</div>
                  <div className="text-[10px] text-slate-500">tradycyjny przelew bankowy</div>
                </button>
              </div>

              {method === "transfer" && bank && (
                <div className="mb-3 rounded-xl border border-teal-500/25 bg-teal-500/8 p-3.5 text-xs leading-relaxed text-slate-300" data-testid="transfer-info">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-500">Nr konta:</span>
                    <span className="font-mono font-bold text-teal-200">{bank.bank_account}</span>
                  </div>
                  <p className="mt-2 text-[11px] text-teal-200/90">
                    Przy tej formie płatności produkt wysyłamy <b>natychmiast po otrzymaniu płatności</b> na nasze konto.
                  </p>
                </div>
              )}

              <button
                data-testid="cart-checkout-button"
                onClick={submit}
                disabled={!items.length || checkingOut}
                className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3.5 font-display text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-40"
              >
                {checkingOut ? "Przetwarzanie..." : method === "transfer" ? "Złóż zamówienie z przelewem" : "Przejdź do płatności"}
              </button>
              <p className="mt-3 text-center text-[11px] text-slate-500">
                {method === "stripe"
                  ? "Karty Visa / Mastercard, BLIK, Przelewy24 — bezpieczna płatność Stripe. Pliki otrzymasz natychmiast po opłaceniu."
                  : "Po zaksięgowaniu przelewu wyślemy produkt e-mailem — natychmiast."}
              </p>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
